// Undoes Enterprise's header-relative body numbering so this app's Templates UI can show/edit a
// template's body starting at {{1}}, the same way every other field in this app (and the
// console) numbers its own variables independently.
//
// Verified cause (ce-api/whatsappmediaapi/src/main/java/com/gupshup/ce/, 2026-09-22):
//   - On create AND update, Enterprise validates the incoming body FIRST — ValidationUtils.java:
//     914-924 rejects a body whose variables don't start at {{1}} ("Variables in template body
//     must start from {{1}}.") — and only THEN calls rearrangeVariables() (models/resource/
//     MessageTemplate.java:1056 on create, :1224 on update).
//   - rearrangeVariables() (MessageTemplate.java:1586-1603): when the header has exactly one
//     variable, the STORED body is renumbered to start at {{2}} instead of {{1}}
//     (TemplateUtils.rearrangeVariables(this.template, 2)); otherwise it's left starting at {{1}}.
//     This is Enterprise's own flat send-time numbering — header variable = 1, body variables =
//     2..n — applied only when Enterprise persists/returns the template, never something this UI
//     asked for.
// Consequence: a template created with header "Order {{1}} update" and body "Hi {{1}}, ..." comes
// back from the list API with body "Hi {{2}}, ...". Loading that body as-is into this form (or
// showing it as-is in the list/detail views) displays {{2}}-based numbering; saving it back
// unchanged sends a body starting at {{2}}, which the FIRST check above rejects outright — so
// every template with both a header variable and body variables was un-editable and un-clonable.
//
// The shift is reversed ONLY when it's provably the shape rearrangeVariables would have produced
// (header has exactly one variable AND the body's own lowest token is {{2}}) — never
// unconditionally. A template with no header variable, or one whose body already starts at {{1}}
// (created some other way, or already normalized), must pass through untouched: shifting it too
// would corrupt a body that was never renumbered in the first place.
const TOKEN_PATTERN = /\{\{(\d+)\}\}/g;

// Distinct {{n}} tokens in `text`, order irrelevant here — only the count matters. Mirrors
// Form/state.js's own extractTokens (which also dedupes) rather than importing it: this helper is
// used from src/helper/ (List.js, DetailModal.js) as well as from Form/ (prefill.js), and
// Form/state.js is form-reducer-specific — pulling pages/Form code into helper/ the other
// direction would be a backwards dependency for the two page-level callers.
function distinctTokenCount(text) {
  if (!text) return 0;
  const seen = new Set();
  let match = TOKEN_PATTERN.exec(text);
  while (match) {
    seen.add(match[0]);
    match = TOKEN_PATTERN.exec(text);
  }
  TOKEN_PATTERN.lastIndex = 0;
  return seen.size;
}

// Every {{n}} token's numeric value in `text`, in document order, duplicates included (the
// minimum is what matters below, not a deduped set).
function tokenNumbers(text) {
  if (!text) return [];
  const numbers = [];
  let match = TOKEN_PATTERN.exec(text);
  while (match) {
    numbers.push(Number(match[1]));
    match = TOKEN_PATTERN.exec(text);
  }
  TOKEN_PATTERN.lastIndex = 0;
  return numbers;
}

// header/body are the raw wire strings (template.header / template.body, or this form's own
// header.headerText.value / body.value — same convention). Returns `body` unchanged unless the
// header-shift condition above is met, in which case every {{n}} in `body` comes back as
// {{n-1}} — only well-formed {{digits}} tokens are touched, everything else in `body` is
// byte-identical to the input.
export function toFormBodyNumbering(header, body) {
  if (!body) return body;
  if (distinctTokenCount(header) !== 1) return body;

  const bodyNumbers = tokenNumbers(body);
  if (bodyNumbers.length === 0) return body;
  if (Math.min(...bodyNumbers) !== 2) return body;

  return body.replace(/\{\{(\d+)\}\}/g, (token, digits) => `{{${Number(digits) - 1}}}`);
}
